import { LogOut, Search, Sparkles, UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { AssistantPanel } from "./AssistantPanel";
import { SyncStatusIndicator } from "./SyncStatusIndicator";

const pageTitles = {
  "/dashboard": { title: "Dispatch Dashboard" },
  "/customers": { title: "Customers" },
  "/records": { title: "Records" },
  "/trucks": { title: "Trucks" },
  "/trailers": { title: "Trailers" },
  "/drivers": { title: "Drivers" },
  "/loads": { title: "Loads" },
  "/loads/import": { title: "Import Loads" },
  "/dispatch-board": { title: "Dispatch Board" },
  "/planning-board": { title: "Planning Board" },
  "/invoices": { title: "Invoices" },
  "/invoices/print": { title: "Invoice Print" },
  "/reports": { title: "Reports" },
  "/users": { title: "Users" },
  "/settings": { title: "Settings" },
};

export const Topbar = ({ user, onLogout }) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");
  const [assistantOpen, setAssistantOpen] = useState(false);
  const syncingFromUrlRef = useRef(false);
  const pathname = location.pathname.startsWith("/customers/") ? "/customers" : location.pathname;
  const currentPage = pageTitles[pathname] || pageTitles["/dashboard"];
  const debouncedSearchInput = useDebouncedValue(searchInput, 250);

  useEffect(() => {
    document.title = `Lumen TMS · ${currentPage.title}`;
  }, [currentPage.title]);

  useEffect(() => {
    const nextSearch = searchParams.get("q") || "";
    if (nextSearch !== searchInput) {
      syncingFromUrlRef.current = true;
      setSearchInput(nextSearch);
    }
  }, [location.pathname, searchParams]);

  useEffect(() => {
    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false;
      return;
    }
    if ((searchParams.get("q") || "") === debouncedSearchInput) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput) {
      nextParams.set("q", debouncedSearchInput);
    } else {
      nextParams.delete("q");
    }
    setSearchParams(nextParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-zinc-200/80 bg-zinc-50/80 px-4 py-4 backdrop-blur md:px-8" data-testid="app-topbar">
      <div className="panel-surface flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
        <div className="min-w-0">
          <h2 className="truncate text-2xl font-semibold text-zinc-900" data-testid="topbar-page-title">{currentPage.title}</h2>
        </div>

        <div className="flex flex-col gap-3 md:min-w-[600px] md:max-w-[900px] md:flex-1 md:flex-row md:items-center md:justify-end">
          <label className="relative block w-full flex-1 md:min-w-[260px]" data-testid="topbar-search-wrapper">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              className="w-full rounded-2xl border border-zinc-200 bg-white py-3 pl-10 pr-4 text-sm text-zinc-900 outline-none transition focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"
              data-testid="topbar-search-input"
              onChange={handleSearchChange}
              placeholder="Search on this page"
              value={searchInput}
            />
          </label>

          <SyncStatusIndicator user={user} />

          {user?.permissions?.includes("assistant") ? (
            <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="topbar-assistant-button" onClick={() => setAssistantOpen(true)} type="button">
              <Sparkles className="h-4 w-4" /> Assistant
            </button>
          ) : null}

          <div className="flex items-center justify-between gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3" data-testid="topbar-user-card">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900 text-white">
                <UserRound className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-900" data-testid="topbar-user-name">{user.name}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500" data-testid="topbar-user-role">{user.role}</p>
              </div>
            </div>

            <button
              className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900"
              data-testid="logout-button"
              onClick={onLogout}
              type="button"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </div>
      <AssistantPanel onOpenChange={setAssistantOpen} open={assistantOpen} />
    </header>
  );
};